import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { reportsApi, type ReportColumnMeta } from '../../api/reports.js';
import { Spinner, ErrorBox, EmptyBox, Field } from '../../components/ui.js';
import { Cell } from '../../components/Cell.js';
import { money } from '../../lib/format.js';

/**
 * 원본: randombox/order/randombox_order_ranking.php
 *
 * 원본은 "당첨현황 대시보드"라는 이름으로, 랜덤박스 선택기 하나를 공유하는 카드
 * 두 개(왼쪽 상품별 · 오른쪽 등급별)를 한 화면에 나란히 보여준다. 데이터 자체는
 * 이미 정의 기반 리포트로 있던 randombox-prize-ranking(왼쪽) ·
 * randombox-grade-ranking(오른쪽)을 그대로 재사용한다 - SQL 을 새로 안 만들고,
 * 두 정의를 같은 randomboxId 로 나란히 그리기만 한다.
 *
 * randombox-grade-ranking 을 독립된 정렬 가능한 표로 따로 보여주던
 * "N등급별 당첨현황" 메뉴·화면은 없앴다 - 그 데이터는 이제 이 대시보드의
 * 오른쪽 카드로만 보인다. report.registry.ts 의 randombox-grade-ranking
 * 정의 자체는 이 카드가 여전히 쓰므로 남겨 둔다.
 */

const DEFAULT_RANDOMBOX_ID = '4001';

export function RandomboxPrizeRankingPage() {
  const [params, setParams] = useSearchParams();
  const randomboxId = params.get('randomboxId') || DEFAULT_RANDOMBOX_ID;

  const meta = useQuery({
    queryKey: ['report', 'randombox-prize-ranking', 'meta'],
    queryFn: () => reportsApi.meta('randombox-prize-ranking'),
    staleTime: 5 * 60 * 1000,
  });

  const boxOptions = meta.data?.filters.find((f) => f.key === 'randomboxId')?.options ?? [];

  return (
    <section>
      <header className="page-head">
        <div>
          <h1 className="page-title">당첨현황 대시보드</h1>
          <p className="page-desc">랜덤박스 당첨 현황 및 통계 분석.</p>
        </div>
      </header>

      <div className="filters">
        <Field label="랜덤박스 선택">
          <select
            className="input"
            value={randomboxId}
            onChange={(e) => {
              const next = new URLSearchParams(params);
              if (e.target.value) next.set('randomboxId', e.target.value);
              else next.delete('randomboxId');
              setParams(next, { replace: true });
            }}
          >
            {boxOptions.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </Field>
      </div>

      <div className="dashboard-grid">
        <div className="panel">
          <h2 className="panel__title">상품별 당첨 현황</h2>
          <RankingTable
            reportId="randombox-prize-ranking"
            randomboxId={randomboxId}
            emptyLabel={`${randomboxId}의 당첨 현황 데이터가 존재하지 않습니다.`}
          />
        </div>

        <div className="panel">
          <h2 className="panel__title">등급별 당첨 현황 및 확률</h2>
          <RankingTable
            reportId="randombox-grade-ranking"
            randomboxId={randomboxId}
            emptyLabel={`${randomboxId}의 당첨 현황 데이터가 존재하지 않습니다.`}
          />
        </div>
      </div>
    </section>
  );
}

/**
 * 정의 기반 리포트 하나를 랜덤박스 하나로 좁혀서 표로만 그린다 - ReportPage 와
 * 달리 정렬 헤더·검색 필터는 없다(원본 대시보드 카드에도 없다).
 */
function RankingTable({
  reportId,
  randomboxId,
  emptyLabel,
}: {
  reportId: string;
  randomboxId: string;
  emptyLabel: string;
}) {
  const meta = useQuery({
    queryKey: ['report', reportId, 'meta'],
    queryFn: () => reportsApi.meta(reportId),
    staleTime: 5 * 60 * 1000,
  });

  const data = useQuery({
    queryKey: ['report', reportId, { randomboxId }],
    queryFn: () => reportsApi.data(reportId, { randomboxId }),
  });

  if (meta.isPending || data.isPending) return <Spinner />;
  if (meta.isError) return <ErrorBox error={meta.error} onRetry={() => void meta.refetch()} />;
  if (data.isError) return <ErrorBox error={data.error} onRetry={() => void data.refetch()} />;
  if (data.data.items.length === 0) return <EmptyBox>{emptyLabel}</EmptyBox>;

  const visibleColumns: ReportColumnMeta[] = meta.data.columns.filter((c) => !c.hidden);

  return (
    <div className="table-wrap">
      <table className="table">
        <thead>
          <tr>
            {visibleColumns.map((c) => (
              <th key={c.key} style={{ textAlign: c.align ?? 'left' }}>
                {c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.data.items.map((row, i) => (
            <tr key={i}>
              {visibleColumns.map((c) => (
                <td key={c.key} style={{ textAlign: c.align ?? 'left' }}>
                  <Cell column={c} row={row} totals={data.data.totals} />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
        {data.data.totals && (
          <tfoot>
            <tr className="row--total">
              {visibleColumns.map((c, idx) => (
                <td key={c.key} style={{ textAlign: c.align ?? 'left' }}>
                  {idx === 0
                    ? '합계'
                    : data.data.totals?.[c.key] !== undefined
                      ? money(data.data.totals[c.key])
                      : ''}
                </td>
              ))}
            </tr>
          </tfoot>
        )}
      </table>
    </div>
  );
}
